import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

// Convert UTC date to IST (UTC+5:30)
const convertToIST = (utcDateString) => {
  // Ensure the string is treated as UTC by adding 'Z' if not present
  let dateString = utcDateString;
  if (!dateString.includes('+') && !dateString.endsWith('Z')) {
    dateString = dateString + 'Z';
  }

  // Parse the UTC date
  const utcDate = new Date(dateString);

  // Convert to IST by adding 5 hours 30 minutes (in milliseconds)
  const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));

  return istDate;
};

const StudentMessageArea = ({
  conversation,
  messages,
  currentUserId,
  onSendMessage,
  onTyping,
  typingUser,
  isConnected,
  onlineUsers,
  lastSeen,
  onBack
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      onTyping(true);
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      onTyping(false);
    }, 1000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newMessage.trim() && isConnected) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
      setIsTyping(false);
      onTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const getOtherParticipant = () => {
    const otherParticipantId = conversation.participants.find(id => id !== currentUserId);
    const isOnline = onlineUsers[otherParticipantId] === true;
    return {
      id: otherParticipantId,
      name: conversation.participant_names[otherParticipantId] || 'Unknown',
      type: conversation.participant_types[otherParticipantId],
      isOnline: isOnline
    };
  };

  const otherParticipant = getOtherParticipant();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-none px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 bg-gray-900 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Back button for mobile */}
            {onBack && (
              <button
                onClick={onBack}
                className="md:hidden p-2 -ml-2 hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Back to conversations"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-400" />
              </button>
            )}
            <div className={`h-10 w-10 rounded-full ${
              otherParticipant.type === 'student' ? 'bg-green-500' : 'bg-blue-500'
            } flex items-center justify-center text-white font-semibold flex-shrink-0`}>
              {otherParticipant.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-medium text-gray-100 truncate">{otherParticipant.name}</h3>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  otherParticipant.type === 'student'
                    ? 'bg-green-900 text-green-300'
                    : 'bg-blue-900 text-blue-300'
                }`}>
                  {otherParticipant.type === 'student' ? 'Student' : 'Staff'}
                </span>
                <span className={`text-xs ${otherParticipant.isOnline ? 'text-green-400' : 'text-gray-500'} truncate`}>
                  {otherParticipant.isOnline ? (
                    '● Online'
                  ) : (
                    lastSeen[otherParticipant.id] ? (
                      (() => {
                        const lastSeenString = lastSeen[otherParticipant.id];
                        const dateString = lastSeenString.includes('+') || lastSeenString.endsWith('Z')
                          ? lastSeenString
                          : lastSeenString + 'Z';
                        return `Last seen ${formatDistanceToNow(parseISO(dateString), { addSuffix: true })}`;
                      })()
                    ) : (
                      '○ Offline'
                    )
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 bg-gray-800">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const isCurrentUser = message.sender_id === currentUserId;

              // Debug logging
              if (index === 0) {
                console.log('💬 Message comparison:', {
                  messageSenderId: message.sender_id,
                  currentUserId: currentUserId,
                  isCurrentUser: isCurrentUser,
                  senderName: message.sender_name
                });
              }

              const messageDate = convertToIST(message.created_at);
              const prevMessageDate = index > 0 ? convertToIST(messages[index - 1].created_at) : null;
              const showTimestamp =
                index === 0 ||
                (prevMessageDate && (messageDate.getTime() - prevMessageDate.getTime() > 300000));

              return (
                <div key={message.id}>
                  {showTimestamp && (
                    <div className="text-center text-xs text-gray-500 my-2">
                      {format(messageDate, 'MMM d, yyyy h:mm a')}
                    </div>
                  )}
                  <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] sm:max-w-sm md:max-w-md lg:max-w-lg ${isCurrentUser ? 'order-2' : 'order-1'}`}>
                      {!isCurrentUser && (
                        <p className="text-xs text-gray-400 mb-1 px-3">{message.sender_name}</p>
                      )}
                      <div
                        className={`rounded-lg px-4 py-2 ${
                          isCurrentUser
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-100 border border-gray-600'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                        <div className={`text-xs mt-1 ${isCurrentUser ? 'text-blue-200' : 'text-gray-400'}`}>
                          {format(messageDate, 'h:mm a')}
                          {isCurrentUser && message.is_read && (
                            <span className="ml-1">✓✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Typing indicator */}
            {typingUser && typingUser.isTyping && typingUser.userId !== currentUserId && (
              <div className="flex justify-start">
                <div className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <div className="flex-none px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-gray-900 border-t border-gray-700 sticky bottom-0 z-10">
        {!isConnected && (
          <div className="mb-2 text-sm text-amber-400 bg-amber-900/20 px-3 py-2 rounded border border-amber-800">
            Reconnecting to chat server...
          </div>
        )}
        <form onSubmit={handleSubmit} className="flex space-x-2 sm:space-x-3">
          <input
            type="text"
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Type a message..."
            className="flex-1 border border-gray-700 bg-gray-800 text-gray-100 rounded-lg px-3 sm:px-4 py-2.5 sm:py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
            disabled={!isConnected}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || !isConnected}
            className="bg-blue-600 text-white px-4 sm:px-6 py-2.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed text-sm sm:text-base font-medium whitespace-nowrap"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentMessageArea;
